import { TEMPLATE_OPTIONS_MAP, TEMPLATE_FIELDS_MAP } from 'constants/template';

export interface Position {
    top: string | number,
    right: string | number,
    bottom: string | number,
    left: string | number,
}

export interface PositionStyles {
    top: string,
    right: string,
    bottom: string,
    left: string,
}

export type QuadrantValue = 'top-left' | 'bottom-left' | 'top-right' | 'bottom-right';

export interface ExtensionState {
    appended: boolean,
    mainVisible: boolean,
    quadrant: QuadrantValue,
    position: Position
}

export enum Sender {
    React,
    Content,
    Background
}

export interface ChromeMessage {
    from: Sender,
    message: any
}

export type SendMessageResponse = (response: any) => void;

export interface WebMessageRequest {
    type: string,
    payload: any
}

export interface ContentMessage {
    key: string,
    action: string,
    payload?: any
}

export type DateTimeFormatKey = 'time' | 'date' | 'longDate' | 'dateTimeShort' | 'dateTime' | 'fullDateShort' | 'fullDate' | 'machineFriendlyFullDate' | 'fullDateTime';

export type UserTimeFormat = '12h' | '24h';

export type UserDateFormat = 'mm/dd/yyyy' | 'dd/mm/yyyy' | 'yyyy/mm/dd';

export type DateTimeFormats = Record<DateTimeFormatKey, string>

export type AllDateTimeFormats = Record<UserDateFormat, DateTimeFormats>;

export type ProjectModules = 'home' | 'milestones' | 'tasks' | 'discussion' | 'documents' | 'files';

export type CustomFieldValue = number | string | DateRange;

export interface Settings {
    timeTrackingEnabled?: boolean,
    notesEnabled?: boolean,
    floatingButtonsEnabled?: boolean,
    // floatinbButtonsDisabledForDomains?: string[]
}

interface AsyncResource {
    fetched?: boolean,
    loading?: boolean,
    error?: boolean
}

export interface FetchState extends AsyncResource {
    hasBefore?: boolean,
    hasAfter?: boolean,
    hasMore?: boolean
}

export type FetchInfo = Record<string, Record<string, FetchState>>;

export type FetchInfoWithoutId = Record<string, FetchState>;

export interface User {
    id: string,
    name: string,
    email: string,
    color: string,
    secondary_color: string,
    initials: string,
    avatar: string,
    date_format: UserDateFormat,
    time_format: UserTimeFormat,
    week_start: string,
    language: string,
    pending?: boolean,
    removed?: boolean,
}

export interface Team {
    id: string,
    name: string,
    url: string,
    color: string,
    secondary_color: string,
    initials: string,
    logo: string,
    current: boolean,
    removed: boolean,
    chat_disabled: boolean
}

export interface Subteam {
    id: string,
    name: string,
    is_general: boolean
}

export type IntegrationProvider = 'Jira';
export type IntegrationExternalId = string | number;

export interface Integration {
    id: string,
    provider: IntegrationProvider,
    externalId: IntegrationExternalId,
    type: 'LabelWise' | 'Overall',
    name: string,
    labelId?: string

}

export interface Project extends AsyncResource {
    id: string,
    name: string,
    subteam: string,
    archived?: boolean,
    removed?: boolean,
    color: string,
    demo?: boolean,
    owner: string,
    members: string[],
    integrations?: Integration[],
    disabled_modules: ProjectModules
}

export interface CustomField {
    id: string,
    name: string,
    type: CustomFieldType,
    currency?: CurrencySymbol,
    options?: {
        id: string,
        value: string,
        color: string
    }[]
}

export interface CustomFieldReport {
    id: string,
    name: string,
    type: CustomFieldType,
    values: CustomFieldValue[],
    field: CustomField
}

export interface TaskGroup {
    id: string,
    name: string,
    project?: string,
    color?: string
}

export interface Task {
    id: string,
    name: string,
    project?: string,
    due_date?: string | number | Date | null,
    story_points?: number,
    tracked_time?: number,
    task_group?: string,
    completed: boolean,
    archived: boolean,
    labels: string[],
    assignees: string[],
    fields: {
        id: string,
        value: CustomFieldValue
    }[]
}

export interface TaskGroupTaskProps {
    assignees?: string[],
    milestone?: string,
    labels?: string[],
    fields?: {
        id: string,
        value: CustomFieldValue
    }[]
}

export interface TaskDragProps {
    id: string,
    taskGroupId: string,
    milestoneId: string,
    completed: boolean,
    archived: boolean,
    order: number,
    taskGroupTaskProps?: TaskGroupTaskProps
}

export interface Milestone {
    id: string,
    name?: string,
    description?: string,
    start?: string | number | Date | null,
    end?: string | number | Date | null,
    project: string
}

export interface TimeEntry {
    id: string,
    active: boolean,
    task: string,
    project?: string,
    start: string,
    user: string,
    end?: string
}

export interface Chat {
    id: string,
    name: string,
    type: 'group' | 'direct',
    project?: string,
    last_message_at: string,
    is_self?: boolean,
    members: {
        id: string,
        is_muted: boolean,
        is_owner: boolean,
        joined: boolean
    }[]
}

export interface MessageAttachment {
    id: string,
    download: string,
    thumbnail: string,
    name: string,
    size: number
}

export interface Message {
    id: string,
    chat: string,
    text: string,
    author: string,
    created_at: string,
    loading?: boolean,
    is_deleted?: boolean,
    hidden?: string[],
    seen_by?: string[],
    bot_name?: string,
    type?: 'text' | 'gif',
    subtype?: string,
    url?: string,
    attachments?: MessageAttachment[]
}

export type NotificationType = 'system' | 'task' | 'subtask' | 'message' | 'thread' | 'document' | 'file';
export type NotificationAction = 'alert' | 'join' | 'leave' | 'invite' | 'archive' | 'invite' | 'tag';
export type NotificationAssociationType = 'Chat';

export interface Notification {
    id: string,
    type: NotificationType,
    action: NotificationAction,
    is_read: boolean,
    created_at: string,
    triggered_by: {
        id: string
    },
    association: {
        id: string,
        type: NotificationAssociationType,
        message_id: string,
        chat_id: string,
        folder_stack?: string[]
    },
    project?: {
        id: string
    }
}

export interface Doc {
    id: string,
    name: string,
    content: any[] | null
}

export type LabelType = 'others' | 'member';

export interface Label {
    id: string,
    type: LabelType,
    name: string,
    removed?: boolean,
    color: string
}

export type FavoriteType = 'module' | 'fileFolder' | 'docFolder' | 'doc' | 'file' | 'projectEmbed' | 'personalTasks' | 'personalNotes';
export type FavoriteResourceId = ProjectModules | string;

export interface Favorite {
    id: string,
    type: FavoriteType,
    created_at: string,
    project_id: string,
    resource_id: FavoriteResourceId,
    order: number,
    name?: string,
    removed?: boolean
}

export type CustomFieldType = 'budget' | 'text' | 'dropdown' | 'reminder' | 'number' | 'url' | 'phone' | 'email' | 'location' | 'currency' | 'checkbox' | 'date' | 'calc';
type DateRange = {
    start: Date,
    end: Date
};

type CalendarRange = Date[] | DateRange;

export type CurrencySymbol = 'usd' | 'eur' | 'gbp' | 'inr' | 'jpy' | 'cad' | 'aud' | 'chf' | 'mxn' | 'try' | 'ils' | 'rub' | 'cny';

export type TasksFilter = {
    term?: string, // search term
    project?: string, // project id
    user?: string, // user id
    dueDate?: DateRange,
    milestone?: string, // milestone id
    label?: string, // label id
    field?: {
        id: string,
        type: CustomFieldType,
        value: CustomFieldValue
    }
}

export type TasksViewConfigType = 'kanban' | 'table' | 'calendar' | 'swimlane' | 'timeline' | 'report';

type TasksGroupByType = 'assignees' | 'dueDate' | 'labels' | 'milestone' | string; // custom field id

export type TasksViewConfig = {
    viewType: TasksViewConfigType,
    selectedProjects?: string[],
    selectProjectsVisible?: boolean,
    filter: TasksFilter,
    showCompleted?: boolean,
    showArchived?: boolean,
    groupBy?: TasksGroupByType,
    collapsedTaskGroups?: string[],
    sortBy?: string | null, // taskField:ASC | taskField:DESC e.g: labels:ASC or customFieldId:DESC
    hiddenColumns?: string[], // table type
    columnsOrder?: string[], // table type
    hiddenFields?: string[], // kanban
    calendarRange?: CalendarRange, // calendar
    calendarPeriod?: 'day' | 'week' | 'month', // calendar
    timelinePeriod?: 'day' | 'week' | 'month' | 'year', // project timeline
    calendarHidden?: boolean // user calendar
}

type TasksViewType = 'myTasks' | 'myTasksCalendar' | 'allTasks' | 'allTasksCalendar' | 'project' | 'workloads';

export interface TasksView {
    id: string,
    type: TasksViewType,
    project_id?: string, // for project view
    config: TasksViewConfig,
    is_auto_save: boolean, // we'll have one user default view for each type and project, where we'll store the last view state, no matter if he saves or not.
    is_default: boolean, // this will be the default view for the type and project_id
    is_shared: boolean, // the project views can be shared with other project members
    is_pinned: boolean,
    name: string,
    created_by: string,
    removed?: boolean
}

type TemplateOptionsTypeMap = {
    'project': typeof TEMPLATE_OPTIONS_MAP.project[number]
    'milestone': typeof TEMPLATE_OPTIONS_MAP.milestone[number]
    'task': typeof TEMPLATE_OPTIONS_MAP.task[number]
    'document': typeof TEMPLATE_OPTIONS_MAP.document[number]
}

export type TemplateType = Extract<keyof typeof TEMPLATE_OPTIONS_MAP, string>;

export type TemplateOptionType = TemplateOptionsTypeMap[TemplateType];

export interface TemplateField {
    type: typeof TEMPLATE_FIELDS_MAP[TemplateType][number],
    offset: number | null
}

export interface TemplateChildResource {
    fields: TemplateField[],
    id: string,
    parent_id?: string,
    milestone_id?: string,
    name: string,
    type: 'milestone' | 'task' | 'subtask'
}

export interface Template {
    id: string,
    resource_id: string,
    type: TemplateType,
    name: string,
    created_by: string,
    description?: string,
    removed?: boolean,
    options: TemplateOptionType[],
    fields: TemplateField[],
    children: {
        id: string,
        fields: TemplateField[]
    }[]
}
